/**
 * AuthContext — single source of truth for the logged-in user, permissions,
 * and session lifecycle.
 *
 * On mount:
 *  - patches window.fetch once to always attach `credentials: 'include'` for
 *    backend-origin requests (so JWT cookies + X-API-Key both work everywhere)
 *  - calls GET /api/auth/whoami to hydrate the current user (JWT cookie or API key)
 *
 * Exposed:
 *  - user: whoami response or null
 *  - loading: bool (initial bootstrap)
 *  - login(email, password) -> user
 *  - logout() -> void
 *  - refresh() -> user
 *  - hasPermission(perm): bool
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { formatBackendDetail } from '../lib/apiErrors';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Install fetch wrapper once — ensures cookies + X-API-Key are always sent
// for calls to the backend API, without touching every component.
// ---------------------------------------------------------------------------
let fetchPatched = false;

// Iter 32f — module-scoped flag tracks whether the CURRENTLY logged-in
// user is Platform Root. Set by AuthProvider on hydrate / login / logout.
// The fetch wrapper consults this BEFORE attaching `X-Active-Tenant-Id`,
// so a stale `qf_platform_active` sessionStorage value never leaks into
// requests made by a tenant Owner / Admin / Operator / Viewer.
let currentActorIsPlatformRoot = false;

function _clearPlatformActive() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('qf_platform_active');
    }
  } catch { /* ignore */ }
}

function installFetchWrapper() {
  if (fetchPatched) return;
  fetchPatched = true;
  const originalFetch = window.fetch.bind(window);

  // P0 demo-safety self-heal helper. When the backend responds with
  // `400 {detail: {error: "active_tenant_invalid"}}` it means the
  // `X-Active-Tenant-Id` we attached from `qf_platform_active` points
  // at a tenant that no longer exists (typical after a redeploy / DB
  // reseed). Without this guard EVERY authenticated route 400s for up
  // to 30 minutes (the sessionStorage idle TTL), bricking the demo.
  // Behaviour: clone the body once, peek for the marker, drop the
  // sessionStorage entry, and retry the SAME request once without the
  // header. The retried response is what the caller sees, so existing
  // call sites stay unchanged.
  async function _isStaleActiveTenant(resp) {
    if (!resp || resp.status !== 400) return false;
    try {
      const cloned = resp.clone();
      const data = await cloned.json();
      const detail = data && data.detail;
      return !!(detail && typeof detail === 'object'
        && detail.error === 'active_tenant_invalid');
    } catch {
      return false;
    }
  }

  window.fetch = async (input, init = {}) => {
    let url = typeof input === 'string' ? input : (input && input.url) || '';
    const isBackend = url.startsWith(API_URL) || url.startsWith('/api');
    if (!isBackend) return originalFetch(input, init);

    const buildInit = (attachActiveTenant) => {
      const nextInit = { ...init };
      if (!nextInit.credentials) nextInit.credentials = 'include';
      const apiKey = (typeof localStorage !== 'undefined' && localStorage.getItem('quod_api_key')) || '';
      const headers = new Headers(nextInit.headers || {});
      if (apiKey && !headers.has('X-API-Key')) headers.set('X-API-Key', apiKey);
      // Iter 32a + 32f — Platform Root active-tenant context. The tenant
      // id is selected via the Platform Console "Act as" button and stored
      // (with a 30-min idle expiry) in sessionStorage. We attach the
      // header ONLY when the CURRENT actor is Platform Root — even though
      // the backend silently ignores it for tenant users, preventing it
      // at the source avoids any possibility of the SPA leaking a stale
      // PR session into a subsequent Owner login (e.g. demo workstations
      // that swap roles in the same browser tab).
      try {
        if (attachActiveTenant && currentActorIsPlatformRoot && typeof sessionStorage !== 'undefined') {
          const raw = sessionStorage.getItem('qf_platform_active');
          if (raw) {
            const obj = JSON.parse(raw);
            const now = Date.now();
            const lastTouch = Number(obj && obj.lastTouch) || 0;
            // 30 min idle clear (matches the iter-32a spec).
            if (obj && obj.id && now - lastTouch < 30 * 60 * 1000) {
              if (!headers.has('X-Active-Tenant-Id')) {
                headers.set('X-Active-Tenant-Id', String(obj.id));
              }
              obj.lastTouch = now;
              sessionStorage.setItem('qf_platform_active', JSON.stringify(obj));
            } else {
              sessionStorage.removeItem('qf_platform_active');
            }
          }
        }
      } catch { /* never let a storage glitch break the fetch */ }
      nextInit.headers = headers;
      return nextInit;
    };

    const firstResp = await originalFetch(input, buildInit(true));
    if (await _isStaleActiveTenant(firstResp)) {
      // Self-heal: drop the stale active-tenant context and retry once
      // without the header. Subsequent calls will skip the header
      // entirely because sessionStorage is now empty.
      _clearPlatformActive();
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('quod:active-tenant-cleared', {
            detail: { reason: 'active_tenant_invalid' },
          }));
        }
      } catch { /* non-fatal */ }
      return originalFetch(input, buildInit(false));
    }
    return firstResp;
  };
}

export function AuthProvider({ children }) {
  installFetchWrapper();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const hydrate = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/whoami`);
      if (!res.ok) {
        if (mountedRef.current) setUser(null);
        // Iter 32f — no authenticated user → cannot be Platform Root,
        // so any lingering active-tenant sessionStorage must be cleared.
        currentActorIsPlatformRoot = false;
        _clearPlatformActive();
        return null;
      }
      const data = await res.json();
      if (mountedRef.current) setUser(data);
      // Iter 32f — keep the fetch-wrapper PR flag in lockstep with the
      // resolved user. If the current user is NOT Platform Root, also
      // clear any stale qf_platform_active from a previous PR session
      // in the same browser tab.
      const isRoot = !!(data && data.is_platform_root);
      currentActorIsPlatformRoot = isRoot;
      if (!isRoot) _clearPlatformActive();
      return data;
    } catch {
      if (mountedRef.current) setUser(null);
      currentActorIsPlatformRoot = false;
      _clearPlatformActive();
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    hydrate();
    return () => { mountedRef.current = false; };
  }, [hydrate]);

  const login = useCallback(async (email, password) => {
    // Iter 32f — clear any stale Platform Root acting-as-tenant context
    // BEFORE attempting login. Even though the new user might also be
    // Platform Root, hydrate() will repopulate the flag correctly. For
    // any non-PR login this prevents the very first whoami request
    // from carrying a stale `X-Active-Tenant-Id` header.
    currentActorIsPlatformRoot = false;
    _clearPlatformActive();
    const res = await fetch(`${API_URL}/api/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      let detail = 'Login failed';
      try {
        const err = await res.json();
        detail = formatBackendDetail(err.detail) || detail;
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    const data = await res.json();
    // MFA path: return challenge payload to the caller — they render the UI.
    if (data.mfa_required) {
      return { mfa_required: true, ...data };
    }
    await hydrate();
    return { mfa_required: false };
  }, [hydrate]);

  const mfaChallenge = useCallback(async (challengeToken, code) => {
    const res = await fetch(`${API_URL}/api/auth/mfa/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    });
    if (!res.ok) {
      let detail = 'MFA challenge failed';
      try { const err = await res.json(); detail = formatBackendDetail(err.detail) || detail; } catch { /* ignore */ }
      throw new Error(detail);
    }
    return hydrate();
  }, [hydrate]);

  const mfaEnrol = useCallback(async (challengeToken) => {
    const res = await fetch(`${API_URL}/api/auth/mfa/setup`, {
      method: 'POST',
      headers: { 'X-MFA-Challenge': challengeToken },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatBackendDetail(err.detail) || 'Enrolment failed');
    }
    return res.json();
  }, []);

  const mfaEnrolVerify = useCallback(async (challengeToken, code) => {
    const res = await fetch(`${API_URL}/api/auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-MFA-Challenge': challengeToken },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatBackendDetail(err.detail) || 'Verify failed');
    }
    // Backend sets cookies if we arrived via X-MFA-Challenge, so we're signed in.
    await hydrate();
    return res.json();
  }, [hydrate]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/users/logout`, { method: 'POST' });
    } catch { /* ignore */ }
    // Also clear any API key so we don't fall back to Owner context silently
    try { localStorage.removeItem('quod_api_key'); } catch { /* ignore */ }
    // Iter 32f — clear Platform Root active-tenant state immediately on
    // logout so a subsequent Owner / Admin / Operator login in the same
    // browser tab does not inherit a stale acting-as-tenant context.
    currentActorIsPlatformRoot = false;
    _clearPlatformActive();
    setUser(null);
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.is_platform_root) return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  }, [user]);

  const value = { user, loading, login, logout, refresh: hydrate, hasPermission,
                  mfaChallenge, mfaEnrol, mfaEnrolVerify };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
